import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheckIcon,
  PlusCircleIcon,
  PencilIcon,
  TrashIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  fetchBpcTenants,
  fetchManagedTenants,
  removeBpcTenant,
} from '../api/client';
import Pagination from './Pagination';
import CopyButton from './CopyButton';
import EditBpcTenantDrawer from './EditBpcTenantDrawer';
import { RefreshButton } from './ui/LoadingState';
import {
  formatDate,
  compareVals,
  applyColFilters,
  countActiveFilters,
  useResizableCols,
  SortIndicator,
  FilterCell,
} from './coverage/coverageShared';

const BPC_COLS = [
  { key: 'msp',            label: 'MSP',              w: 80,  align: 'left', filterType: 'select'  },
  { key: 'bpcAccountName', label: 'BPC Account Name', w: 180, align: 'left', filterType: 'text'    },
  { key: 'bpcTenantName',  label: 'BPC Tenant Name',  w: 220, align: 'left', filterType: 'text'    },
  { key: 'bpcTenantId',    label: 'BPC Tenant ID',    w: 140, align: 'left', filterType: 'text'    },
  { key: 'bpcAccountId',   label: 'BPC Account ID',   w: 140, align: 'left', filterType: 'text'    },
  { key: 'm365Display',    label: 'M365 Mapping',     w: 220, align: 'left', filterType: 'select'  },
  { key: 'notes',          label: 'Notes',            w: 220, align: 'left', filterType: 'text'    },
  { key: 'updatedUtc',     label: 'Updated',          w: 140, align: 'left', filterType: 'date-op' },
];

const ACTIONS_COL_WIDTH = 96;

const initFilters = () => ({
  msp: '', bpcAccountName: '', bpcTenantName: '', bpcTenantId: '', bpcAccountId: '',
  m365Display: '', notes: '',
  updatedUtc: '', updatedUtc_op: '>=',
});

export default function BpcTenantsManagement() {
  const [bpcTenants, setBpcTenants]     = useState([]);
  const [m365Tenants, setM365Tenants]   = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);

  const [filters, setFilters]           = useState(initFilters());
  const [sortField, setSortField]       = useState('bpcAccountName');
  const [sortDir, setSortDir]           = useState('asc');
  const [currentPage, setCurrentPage]   = useState(1);
  const [pageSize, setPageSize]         = useState(10);

  const [drawerOpen, setDrawerOpen]     = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting]           = useState(false);

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [bpc, m365] = await Promise.all([
        fetchBpcTenants().catch(() => []),
        fetchManagedTenants().catch(() => []),
      ]);
      setBpcTenants(Array.isArray(bpc) ? bpc : []);
      setM365Tenants(Array.isArray(m365) ? m365 : []);
    } catch (err) {
      setError(err.message || 'Failed to load BPC tenants.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  // Build m365 lookup map for friendly-name display
  const m365Map = useMemo(() => {
    const m = new Map();
    m365Tenants.forEach((t) => m.set(t.tenantId, t));
    return m;
  }, [m365Tenants]);

  const decorated = useMemo(() => {
    return bpcTenants.map((b) => {
      const m = b.m365TenantId ? m365Map.get(b.m365TenantId) : null;
      const m365Display = m
        ? (m.friendlyName || m.domain || b.m365TenantId)
        : (b.m365TenantId ? `${b.m365TenantId} (not in M365 list)` : '');
      return { ...b, m365Display };
    });
  }, [bpcTenants, m365Map]);

  const distinct = useMemo(() => ({
    msp: [...new Set(decorated.map((r) => r.msp).filter(Boolean))].sort(),
    m365Display: [...new Set(decorated.map((r) => r.m365Display).filter(Boolean))].sort(),
  }), [decorated]);

  const filtered = useMemo(() => {
    let rows = applyColFilters(decorated, BPC_COLS, filters);
    if (sortField) rows = [...rows].sort((a, b) => compareVals(a[sortField], b[sortField], sortDir));
    return rows;
  }, [decorated, filters, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleSort = useCallback((field) => {
    setSortField((prev) => {
      if (prev === field) { setSortDir((d) => d === 'asc' ? 'desc' : 'asc'); return field; }
      setSortDir('asc');
      return field;
    });
    setCurrentPage(1);
  }, []);

  const setFilter = useCallback((key, value) => {
    setFilters((p) => ({ ...p, [key]: value }));
    setCurrentPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(initFilters());
    setCurrentPage(1);
  }, []);

  const activeFilterCount = countActiveFilters(filters);

  // Column widths — include the trailing Actions column so it resizes with the rest
  const { colWidths, thRefs, tableRef, startResize } = useResizableCols([
    ...BPC_COLS.map((c) => c.w),
    ACTIONS_COL_WIDTH,
  ]);

  const handleAdd = () => {
    setEditingTenant(null);
    setDrawerOpen(true);
  };

  const handleEdit = (t) => {
    setEditingTenant(t);
    setDrawerOpen(true);
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await removeBpcTenant(confirmDelete.bpcTenantId);
      setConfirmDelete(null);
      await loadAll();
    } catch (err) {
      setError(err.message || 'Failed to delete BPC tenant.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-6 space-y-6">

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-gray-500 mb-1">
            <Link to="/admin/tenants" className="hover:text-gray-300">Administration / Tenants</Link>
          </div>
          <h1 className="text-xl font-semibold text-gray-100 flex items-center gap-2">
            <ShieldCheckIcon className="h-5 w-5 text-emerald-400" />
            BlackPoint Cyber MDR Tenants
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage <code className="text-xs px-1 py-0.5 bg-gray-800 rounded">BpcAccounts.csv</code> and map BPC tenants to M365 tenants for device inventory comparison.
          </p>
        </div>
        <div className="flex gap-2">
          <RefreshButton
            loading={loading}
            onClick={loadAll}
            className="btn-secondary text-xs px-3 py-1.5 flex items-center gap-1.5"
            iconClassName="h-4 w-4"
          />
          <button
            type="button"
            onClick={handleAdd}
            className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1.5"
          >
            <PlusCircleIcon className="h-4 w-4" />
            Add BPC Tenant
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-3 p-3 bg-red-900/30 border border-red-700/50 rounded-lg text-sm text-red-300">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-red-400" />
          <span className="flex-1">{error}</span>
          <button onClick={loadAll} className="text-xs underline hover:no-underline">Retry</button>
        </div>
      )}

      {/* Filters toolbar */}
      {activeFilterCount > 0 && (
        <div className="flex items-center justify-between -mt-2">
          <span className="text-xs text-gray-500">
            {filtered.length} {filtered.length === 1 ? 'tenant' : 'tenants'} (filtered from {decorated.length})
          </span>
          <button
            type="button"
            onClick={clearFilters}
            className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-200 border border-gray-700 hover:border-gray-600 rounded px-2 py-0.5 transition-colors"
          >
            <XMarkIcon className="h-3 w-3" />
            Clear {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''}
          </button>
        </div>
      )}

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table
            ref={tableRef}
            className="text-xs text-gray-300"
            style={{ tableLayout: 'fixed', width: `${colWidths.reduce((a, b) => a + b, 0)}px` }}
          >
            <thead>
              <tr className="border-b border-gray-800 bg-gray-900/60">
                {BPC_COLS.map((col, i) => (
                  <th
                    key={col.key}
                    ref={(el) => { thRefs.current[i] = el; }}
                    style={{ width: `${colWidths[i]}px` }}
                    className="px-3 py-2 font-medium text-gray-400 whitespace-nowrap relative select-none cursor-pointer text-left"
                    onClick={() => handleSort(col.key)}
                  >
                    <span className="text-xs">{col.label}</span>
                    <SortIndicator active={sortField === col.key} dir={sortDir} />
                    <div
                      className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group"
                      onMouseDown={(e) => { e.stopPropagation(); startResize(e, i); }}
                    >
                      <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                    </div>
                  </th>
                ))}
                <th
                  ref={(el) => { thRefs.current[BPC_COLS.length] = el; }}
                  style={{ width: `${colWidths[BPC_COLS.length]}px` }}
                  className="px-3 py-2 font-medium text-gray-400 whitespace-nowrap relative text-right"
                >
                  <span className="text-xs">Actions</span>
                  <div
                    className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group"
                    onMouseDown={(e) => { e.stopPropagation(); startResize(e, BPC_COLS.length); }}
                  >
                    <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                  </div>
                </th>
              </tr>
              <tr className="border-b border-gray-800 bg-gray-900/30">
                {BPC_COLS.map((col, i) => (
                  <td key={col.key} style={{ width: `${colWidths[i]}px` }} className="px-1.5 py-1">
                    <FilterCell col={col} filters={filters} onFilterChange={setFilter} distinct={distinct} />
                  </td>
                ))}
                <td style={{ width: `${colWidths[BPC_COLS.length]}px` }} className="px-1.5 py-1" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={BPC_COLS.length + 1} className="px-3 py-8 text-center text-gray-500">
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500" />
                    Loading…
                  </div>
                </td></tr>
              ) : paged.length === 0 ? (
                <tr><td colSpan={BPC_COLS.length + 1} className="px-3 py-8 text-center text-gray-500">
                  {bpcTenants.length === 0 ? 'No BPC tenants yet — click "Add BPC Tenant" to start.' : 'No tenants match the current filters.'}
                </td></tr>
              ) : paged.map((t) => (
                <tr key={t.bpcTenantId} className="border-b border-gray-800/50 hover:bg-gray-800/30 group">
                  <td className="px-3 py-2">
                    {t.msp
                      ? <span className="inline-flex items-center px-1.5 py-0.5 text-xs rounded border bg-sky-900/40 text-sky-300 border-sky-700/50">{t.msp}</span>
                      : <span className="text-gray-600">—</span>}
                  </td>
                  <td className="px-3 py-2 text-gray-200 font-medium truncate" title={t.bpcAccountName}>
                    {t.bpcAccountName || '—'}
                  </td>
                  <td className="px-3 py-2 text-gray-300 truncate" title={t.bpcTenantName}>
                    {t.bpcTenantName || '—'}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {t.bpcTenantId
                      ? <span className="inline-flex items-center gap-1">
                          <span className="text-gray-400">{t.bpcTenantId.slice(0, 8)}…</span>
                          <CopyButton text={t.bpcTenantId} />
                        </span>
                      : <span className="text-gray-600">—</span>}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {t.bpcAccountId
                      ? <span className="inline-flex items-center gap-1">
                          <span className="text-gray-400">{t.bpcAccountId.slice(0, 8)}…</span>
                          <CopyButton text={t.bpcAccountId} />
                        </span>
                      : <span className="text-gray-600">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    {t.m365TenantId
                      ? <span className="inline-flex items-center px-1.5 py-0.5 text-xs rounded border bg-blue-900/40 text-blue-300 border-blue-700/50 truncate max-w-full" title={t.m365Display}>
                          {t.m365Display}
                        </span>
                      : <span className="inline-flex items-center px-1.5 py-0.5 text-xs rounded border bg-amber-900/40 text-amber-300 border-amber-700/50">
                          Unmapped
                        </span>}
                  </td>
                  <td className="px-3 py-2 text-gray-400 truncate" title={t.notes}>{t.notes || '—'}</td>
                  <td className="px-3 py-2 text-gray-400">{formatDate(t.updatedUtc)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => handleEdit(t)}
                      className="text-blue-400 hover:text-blue-300 p-1 transition-colors"
                      title="Edit"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(t)}
                      className="text-red-400 hover:text-red-300 p-1 ml-1 transition-colors"
                      title="Delete"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!loading && filtered.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={filtered.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
          />
        )}
      </div>

      {/* Edit/Add drawer */}
      {drawerOpen && (
        <EditBpcTenantDrawer
          tenant={editingTenant}
          m365Tenants={m365Tenants}
          existingBpcIds={bpcTenants.map((t) => t.bpcTenantId)}
          onClose={() => setDrawerOpen(false)}
          onSaved={async () => {
            setDrawerOpen(false);
            await loadAll();
          }}
        />
      )}

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-lg max-w-md w-full p-6">
            <h3 className="text-base font-semibold text-gray-100 mb-2">Delete BPC tenant?</h3>
            <p className="text-sm text-gray-400 mb-4">
              <span className="text-gray-200 font-medium">{confirmDelete.bpcTenantName || confirmDelete.bpcTenantId}</span>{' '}
              will be removed from <code className="text-xs px-1 py-0.5 bg-gray-800 rounded">BpcAccounts.csv</code>.
              This cannot be undone from the portal.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                disabled={deleting}
                className="btn-secondary text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="btn-danger text-sm"
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
